/**
 * client/src/features/income/components/AddIncome/SalaryForm.jsx
 * 
 * Form component for adding salary/W2 income.
 * Supports both pay stub upload and manual entry options.
 */
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  ArrowLeftIcon, 
  DocumentArrowUpIcon, 
  CheckCircleIcon, 
  DocumentTextIcon, 
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, formatDate } from '../../utils/incomeUtils';
import { calculateTaxSummary, formatTaxSummary } from '../../utils/taxCalculator';
import useIncome from '../../hooks/useIncome';
import documentProcessor from '../../services/documentProcessor';

// Frequency options for salary income
const FREQUENCY_OPTIONS = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'biweekly', label: 'Bi-Weekly' },
  { id: 'monthly', label: 'Monthly' }
];

// State options for tax calculation
const STATE_OPTIONS = [
  { id: 'AL', label: 'Alabama' },
  { id: 'AK', label: 'Alaska' },
  { id: 'AZ', label: 'Arizona' },
  { id: 'AR', label: 'Arkansas' },
  { id: 'CA', label: 'California' },
  { id: 'CO', label: 'Colorado' },
  { id: 'CT', label: 'Connecticut' },
  { id: 'DE', label: 'Delaware' },
  { id: 'FL', label: 'Florida' },
  { id: 'GA', label: 'Georgia' },
  { id: 'HI', label: 'Hawaii' },
  { id: 'ID', label: 'Idaho' },
  { id: 'IL', label: 'Illinois' },
  { id: 'IN', label: 'Indiana' },
  { id: 'IA', label: 'Iowa' },
  { id: 'KS', label: 'Kansas' },
  { id: 'KY', label: 'Kentucky' },
  { id: 'LA', label: 'Louisiana' },
  { id: 'ME', label: 'Maine' },
  { id: 'MD', label: 'Maryland' },
  { id: 'MA', label: 'Massachusetts' },
  { id: 'MI', label: 'Michigan' },
  { id: 'MN', label: 'Minnesota' },
  { id: 'MS', label: 'Mississippi' },
  { id: 'MO', label: 'Missouri' },
  { id: 'MT', label: 'Montana' },
  { id: 'NE', label: 'Nebraska' },
  { id: 'NV', label: 'Nevada' },
  { id: 'NH', label: 'New Hampshire' },
  { id: 'NJ', label: 'New Jersey' },
  { id: 'NM', label: 'New Mexico' },
  { id: 'NY', label: 'New York' },
  { id: 'NC', label: 'North Carolina' },
  { id: 'ND', label: 'North Dakota' },
  { id: 'OH', label: 'Ohio' },
  { id: 'OK', label: 'Oklahoma' },
  { id: 'OR', label: 'Oregon' },
  { id: 'PA', label: 'Pennsylvania' },
  { id: 'RI', label: 'Rhode Island' },
  { id: 'SC', label: 'South Carolina' },
  { id: 'SD', label: 'South Dakota' },
  { id: 'TN', label: 'Tennessee' },
  { id: 'TX', label: 'Texas' },
  { id: 'UT', label: 'Utah' },
  { id: 'VT', label: 'Vermont' },
  { id: 'VA', label: 'Virginia' },
  { id: 'WA', label: 'Washington' },
  { id: 'WV', label: 'West Virginia' },
  { id: 'WI', label: 'Wisconsin' },
  { id: 'WY', label: 'Wyoming' },
  { id: 'DC', label: 'District of Columbia' }
];

/**
 * Format a number as currency with commas and 2 decimal places
 * Only used when field loses focus, not during typing
 */
const formatAsCurrency = (value) => {
  if (!value) return '';
  
  // Ensure value is a string before calling replace
  const stringValue = typeof value === 'string' ? value : String(value);
  
  // Remove all non-numeric characters except decimal point
  let number = stringValue.replace(/[^0-9.]/g, '');
  
  // Ensure only one decimal point
  const parts = number.split('.');
  if (parts.length > 2) {
    number = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Parse to float and format with commas and 2 decimal places
  let formatted = '';
  if (number) {
    const num = parseFloat(number);
    if (!isNaN(num)) {
      // Format with commas for thousands
      formatted = num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } else {
      formatted = number; // Keep as is if not a valid number yet
    }
  }
  
  return formatted;
};

/**
 * Parse a currency string to a number
 */
const parseCurrency = (value) => {
  if (!value) return 0;
  // Ensure value is a string before calling replace
  const stringValue = typeof value === 'string' ? value : String(value);
  return parseFloat(stringValue.replace(/,/g, ''));
};

/**
 * SalaryForm Component
 * 
 * Form for adding salary/W2 income with both manual entry and document upload options.
 * Also handles editing existing salary income.
 * 
 * @param {Object} props
 * @param {string} props.bucketId - ID of the bucket to add income to
 * @param {Object} props.income - Income object for editing (optional)
 * @param {boolean} props.isEditing - Flag indicating if we're in edit mode
 * @param {Function} props.onSuccess - Callback for successful form submission
 * @param {Function} props.onBack - Callback to go back to the previous step
 */
const InfoIcon = ({ text }) => (
  <span className="relative inline-flex items-center group align-middle cursor-help" tabIndex={0} aria-label={text}>
    <InformationCircleIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
    <span className="pointer-events-none absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-1 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100 shadow-lg">
      {text}
    </span>
  </span>
);

const SalaryForm = ({ bucketId, income = null, isEditing, onSuccess, onBack }) => {
  // Determine if we're in edit mode
  isEditing = isEditing || !!income;
  
  // Prepare default values based on edit mode
  const getDefaultValues = () => {
    if (isEditing && income) {
      // Normalize/derive commonly missing fields from persisted structures
      const getFromPayDetails = (key) =>
        income?.typeSpecificDetails?.salary?.payDetails?.[key] ??
        income?.typeSpecificDetails?.payDetails?.[key];

      const normalizeFreq = (f) => {
        const v = String(f || '').toLowerCase();
        if (!v) return '';
        if (v.includes('bi') && v.includes('week')) return 'biweekly';
        if (v.includes('week')) return 'weekly';
        if (v.includes('month')) return 'monthly';
        if (v.includes('year') || v.includes('annual')) return 'yearly';
        return v;
      };

      const initialGrossAmount = (
        income.grossAmount ??
        income.amount ??
        getFromPayDetails('grossAmount') ??
        ''
      );

      const initialFrequency = normalizeFreq(
        income.frequency ?? getFromPayDetails('frequency')
      );

      // Prefer explicit annual salary, else derive from grossAmount + frequency
      let derivedGrossAnnual = (
        income.grossAnnualSalary ?? getFromPayDetails('grossAnnualSalary')
      );
      if (!derivedGrossAnnual && initialGrossAmount && initialFrequency) {
        const n = Number(initialGrossAmount) || 0;
        const annual = initialFrequency === 'weekly' ? n * 52
          : initialFrequency === 'biweekly' ? n * 26
          : initialFrequency === 'monthly' ? n * 12
          : n;
        derivedGrossAnnual = annual;
      }

      // Prefer nested, then top-level deductions and W4
      const nestedDeductions = income?.typeSpecificDetails?.salary?.deductions
        ?? income?.typeSpecificDetails?.deductions
        ?? income?.deductions
        ?? {};
      const nestedW4 = income?.typeSpecificDetails?.salary?.w4
        ?? income?.typeSpecificDetails?.w4
        ?? income?.w4
        ?? {};

      const nestedEquity = income?.typeSpecificDetails?.salary?.equity
        ?? income?.typeSpecificDetails?.equity
        ?? income?.equity
        ?? {};

      return {
        name: income.name || '',
        employerName: income.employerName || (income.employer || ''),
        grossAmount: initialGrossAmount,
        grossAnnualSalary: derivedGrossAnnual || '',
        netAmount: income.netAmount || '',
        frequency: initialFrequency || '',
        state: (income.location && income.location.state) || '',
        startDate: income.startDate ? new Date(income.startDate).toISOString().split('T')[0] : '',
        w4: {
          filingStatus: nestedW4.filingStatus || 'SINGLE',
          dependents: String(nestedW4.dependents ?? '0'),
          otherIncome: nestedW4.otherIncome || '',
          deductions: nestedW4.deductions || '',
          extraWithholding: nestedW4.extraWithholding || ''
        },
        taxWithholdings: {
          federal: (income.taxWithholdings && income.taxWithholdings.federal) || '',
          state: (income.taxWithholdings && income.taxWithholdings.state) || '',
          medicare: (income.taxWithholdings && income.taxWithholdings.medicare) || '',
          socialSecurity: (income.taxWithholdings && income.taxWithholdings.socialSecurity) || ''
        },
        deductions: {
          retirement: nestedDeductions.retirement || '',
          health: nestedDeductions.health || '',
          dental: nestedDeductions.dental || '',
          vision: nestedDeductions.vision || '',
          hsa: nestedDeductions.hsa || '',
          fsa: nestedDeductions.fsa || '',
          other: nestedDeductions.other || ''
        },
        notes: income.notes || '',
        // Bonuses
        bonuses: {
          signing: income?.typeSpecificDetails?.salary?.bonuses?.signing || '',
          retention: income?.typeSpecificDetails?.salary?.bonuses?.retention || '',
          performance: income?.typeSpecificDetails?.salary?.bonuses?.performance || '',
          annualTargetPercent: income?.typeSpecificDetails?.salary?.bonuses?.annualTargetPercent || '',
          annualTargetFixed: income?.typeSpecificDetails?.salary?.bonuses?.annualTargetFixed || ''
        },
        // Equity
        equity: {
          totalRSUShares: nestedEquity.totalRSUShares || '',
          rsuGrantUsd: nestedEquity.rsuGrantUsd || '',
          vestStartDate: nestedEquity.vestStartDate ? new Date(nestedEquity.vestStartDate).toISOString().split('T')[0] : '',
          vestCliffMonths: String(nestedEquity.vestCliffMonths ?? '12'),
          vestMonths: String(nestedEquity.vestMonths ?? '48'),
          vestCadence: nestedEquity.vestCadence || 'monthly',
          estimatedSharePrice: nestedEquity.estimatedSharePrice || '',
          annualValue: nestedEquity.annualValue || '',
          vestScheduleJson: JSON.stringify(nestedEquity.vestSchedule || [], null, 2)
        }
      };
    }
    
    return {
      name: '',
      employerName: '',
      grossAmount: '',
      grossAnnualSalary: '',
      netAmount: '',
      frequency: '',
      state: '',
      startDate: '',
      w4: {
        filingStatus: 'SINGLE',
        dependents: '0',
        otherIncome: '',
        deductions: '',
        extraWithholding: ''
      },
      taxWithholdings: {
        federal: '',
        state: '',
        medicare: '',
        socialSecurity: ''
      },
      deductions: {
        retirement: '',
        health: '',
        dental: '',
        vision: '',
        hsa: '',
        fsa: '',
        other: ''
      },
      notes: '',
      bonuses: {
        signing: '',
        retention: '',
        performance: '',
        annualTargetPercent: '',
        annualTargetFixed: ''
      },
      equity: {
        totalRSUShares: '',
        rsuGrantUsd: '',
        vestStartDate: '',
        vestCliffMonths: '12',
        vestMonths: '48',
        vestCadence: 'monthly',
        estimatedSharePrice: '',
        annualValue: '',
        vestScheduleJson: '[]'
      }
    };
  };
  
  // Form state using React Hook Form
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    defaultValues: getDefaultValues()
  });
  
  // Additional state
  const [entryMethod, setEntryMethod] = useState('manual');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  
  // Get income management functions from custom hook
  const { createIncome, updateIncome } = useIncome();
  
  // Watch form values for tax calculation
  const watchedGrossAmount = watch('grossAmount');
  const watchedGrossAnnualSalary = watch('grossAnnualSalary');
  const watchedFrequency = watch('frequency');
  const watchedState = watch('state');
  // Bonuses & employer costs & equity watchers
  const watchedBonuses = watch('bonuses');
  const watchedEquity = watch('equity');
  
  // Get deduction values for tax calculation
  const watchedRetirement = watch('deductions.retirement');
  const watchedHealth = watch('deductions.health');
  const watchedDental = watch('deductions.dental');
  const watchedVision = watch('deductions.vision');
  const watchedOtherDeductions = watch('deductions.other');
  
  // Watch W4 values for tax calculation
  const watchedFilingStatus = watch('w4.filingStatus');
  const watchedDependents = watch('w4.dependents');
  const watchedOtherIncome = watch('w4.otherIncome');
  const watchedDeductions = watch('w4.deductions');
  const watchedExtraWithholding = watch('w4.extraWithholding');
  
  // Parse for tax calculation preview
  const parseForTaxCalculation = (value) => {
    if (!value) return 0;
    // Add type checking before calling replace
    if (typeof value !== 'string') {
      // Handle the case where value might be a number already
      return typeof value === 'number' ? value : 0;
    }
    // Ensure it's a string before calling replace
    const stringValue = String(value);
    return parseFloat(stringValue.replace(/,/g, '')) || 0;
  };
  
  // Calculate tax preview based on current form values including deductions and W4 info
  const previewTaxSummary = calculateTaxSummary({
    type: 'SALARY',
    amount: parseForTaxCalculation(watchedGrossAmount),
    frequency: watchedFrequency,
    state: watchedState,
    deductions: {
      retirement: parseForTaxCalculation(watchedRetirement),
      health: parseForTaxCalculation(watchedHealth),
      dental: parseForTaxCalculation(watchedDental),
      vision: parseForTaxCalculation(watchedVision),
      other: parseForTaxCalculation(watchedOtherDeductions)
    },
    w4: {
      filingStatus: watchedFilingStatus,
      dependents: parseForTaxCalculation(watchedDependents),
      otherIncome: parseForTaxCalculation(watchedOtherIncome),
      deductions: parseForTaxCalculation(watchedDeductions),
      extraWithholding: parseForTaxCalculation(watchedExtraWithholding),
      frequency: watchedFrequency
    }
  });
  
  // Normalize frequency for label formatting
  const normalizeFrequencyForLabel = (freq) => {
    const f = String(freq || '').toUpperCase();
    if (f === 'BIWEEKLY' || f === 'BI-WEEKLY') return 'BI_WEEKLY';
    if (f === 'SEMI-MONTHLY' || f === 'SEMIMONTHLY') return 'SEMI_MONTHLY';
    if (f === 'MONTHLY') return 'MONTHLY';
    if (f === 'WEEKLY') return 'WEEKLY';
    if (f === 'QUARTERLY') return 'QUARTERLY';
    return 'ANNUAL';
  };
  
  // Format tax summary for display
  const formattedTaxSummary = formatTaxSummary(
    previewTaxSummary, 
    parseForTaxCalculation(watchedGrossAmount),
    normalizeFrequencyForLabel(watchedFrequency)
  );

  // Net annual preview (from tax summary)
  const netAnnualPreview = previewTaxSummary?.netAnnual || 0;

  // Total compensation preview (client-side estimate)
  const toAnnual = (amount, frequency) => {
    const n = parseForTaxCalculation(amount);
    const f = (frequency || '').toLowerCase();
    switch (f) {
      case 'weekly': return n * 52;
      case 'biweekly': return n * 26;
      case 'monthly': return n * 12;
      case 'yearly':
      case 'annually': return n;
      default: return n;
    }
  };
  const baseAnnualPreview = toAnnual(watchedGrossAmount, watchedFrequency);
  const annualBonusFromPct = baseAnnualPreview * ((parseForTaxCalculation(watch('bonuses.annualTargetPercent')) || 0) / 100);
  const annualBonusFixed = parseForTaxCalculation(watch('bonuses.annualTargetFixed'));
  const performanceBonus = parseForTaxCalculation(watch('bonuses.performance'));
  const signingBonus = parseForTaxCalculation(watch('bonuses.signing'));
  const retentionBonus = parseForTaxCalculation(watch('bonuses.retention'));
  const annualBonusPreview = Math.max(annualBonusFixed, annualBonusFromPct) + performanceBonus;
  const oneTimeBonusesAnnualized = signingBonus + retentionBonus;
  const equityAnnualEstimated = parseForTaxCalculation(watch('equity.annualValue'));
  const totalCashAnnual = baseAnnualPreview + annualBonusPreview + oneTimeBonusesAnnualized;
  const totalCompAnnual = totalCashAnnual + equityAnnualEstimated;
  
  // Auto-populate tax withholdings when tax preview changes
  useEffect(() => {
    // Only calculate if we have the minimum required fields
    if (watchedGrossAmount && watchedFrequency && watchedState) {
      console.log('Tax preview calculation:', { 
        previewTaxSummary,
        federalWithholding: previewTaxSummary.periodFederalWithholding,
        grossAmount: parseForTaxCalculation(watchedGrossAmount),
        frequency: watchedFrequency,
        filingStatus: watchedFilingStatus
      });
      
      // Set the tax withholding fields based on the preview calculations
      setValue('taxWithholdings.federal', formatAsCurrency(previewTaxSummary.periodFederalWithholding.toString()));
      setValue('taxWithholdings.state', formatAsCurrency(previewTaxSummary.periodStateWithholding.toString()));
      setValue('taxWithholdings.medicare', formatAsCurrency(previewTaxSummary.periodMedicareEE.toString()));
      setValue('taxWithholdings.socialSecurity', formatAsCurrency(previewTaxSummary.periodSocialSecurityEE.toString()));
      
      // Ensure Federal Tax is set to a reasonable value for demonstration purposes
      // This is a fallback to ensure something appears in the field
      const federalAmount = previewTaxSummary.periodFederalWithholding;
      if (parseForTaxCalculation(watchedGrossAmount) > 1500 && federalAmount < 10) {
        // Set a minimum federal tax amount of ~5% of gross for demonstration
        const minimumFederalTax = parseForTaxCalculation(watchedGrossAmount) * 0.05;
        setValue('taxWithholdings.federal', formatAsCurrency(minimumFederalTax.toString()));
      }
      
      // Calculate estimated net pay (gross minus total tax withholdings and deductions)
      const grossAmount = parseForTaxCalculation(watchedGrossAmount);
      const totalDeductions = 
        parseForTaxCalculation(watchedRetirement) +
        parseForTaxCalculation(watchedHealth) +
        parseForTaxCalculation(watchedDental) +
        parseForTaxCalculation(watchedVision) +
        parseForTaxCalculation(watchedOtherDeductions);
      
      const estimatedNetPay = grossAmount - previewTaxSummary.periodTotalWithholdings - totalDeductions;
      
      // Format and set the net amount field
      if (estimatedNetPay > 0) {
        setValue('netAmount', formatAsCurrency(estimatedNetPay.toString()));
      }
    }
  }, [
    watchedGrossAmount, 
    watchedFrequency, 
    watchedState,
    watchedFilingStatus,
    watchedDependents,
    watchedOtherIncome,
    watchedDeductions,
    watchedExtraWithholding,
    watchedRetirement,
    watchedHealth,
    watchedDental,
    watchedVision,
    watchedOtherDeductions,
    previewTaxSummary,
    setValue
  ]);
  
  // Handle tab selection (manual entry vs. upload)
  const handleTabSelect = (method) => {
    setEntryMethod(method);
  };
  
  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadedFile(file);
    setIsProcessing(true);
    setProcessingError(null);
    
    // Simulate processing for now
    // In a real implementation, this would call a document processing service
    setTimeout(() => {
      try {
        // Simulated data extraction
        const extractedData = {
          employerName: 'ACME Corporation',
          grossAmount: '4200.00',
          netAmount: '3150.00',
          taxWithholdings: {
            federal: '650.00',
            state: '250.00',
            medicare: '60.00',
            socialSecurity: '260.00'
          },
          deductions: {
            retirement: '210.00',
            health: '180.00',
            other: '40.00'
          }
        };
        
        setProcessedData(extractedData);
        
        // Populate form with extracted data
        setValue('employerName', extractedData.employerName);
        setValue('grossAmount', extractedData.grossAmount);
        setValue('netAmount', extractedData.netAmount);
        setValue('taxWithholdings.federal', extractedData.taxWithholdings.federal);
        setValue('taxWithholdings.state', extractedData.taxWithholdings.state);
        setValue('taxWithholdings.medicare', extractedData.taxWithholdings.medicare);
        setValue('taxWithholdings.socialSecurity', extractedData.taxWithholdings.socialSecurity);
        setValue('deductions.retirement', extractedData.deductions.retirement);
        setValue('deductions.health', extractedData.deductions.health);
        setValue('deductions.other', extractedData.deductions.other);
        
        setIsProcessing(false);
      } catch (err) {
        console.error('Error processing document:', err);
        setProcessingError('Failed to process the document. Please try manual entry.');
        setIsProcessing(false);
      }
    }, 2000);
  };
  
  // Format currency on blur for each money field
  const handleCurrencyBlur = (fieldName) => (e) => {
    const value = e.target.value;
    if (value) {
      const formatted = formatAsCurrency(value);
      setValue(fieldName, formatted);
    }
  };
  
  // Calculate effective tax rate
  const calculateEffectiveTaxRate = (tax, income) => {
    if (income === 0) return 0;
    return (tax / income * 100).toFixed(1);
  };

  // Handle bidirectional calculation between gross pay and gross annual salary
  const handleGrossPayChange = (value) => {
    const grossAmount = parseFloat(String(value || '').replace(/,/g, '')) || 0;
    const frequency = watch('frequency');
    
    // Validation: Check for reasonable gross amount limits
    if (grossAmount < 0) {
      console.warn('Gross amount cannot be negative');
      return;
    }
    if (grossAmount > 1000000) {
      console.warn('Gross amount exceeds reasonable limit');
    }
    
    if (grossAmount && frequency) {
      let annualAmount = 0;
      switch (frequency) {
        case 'weekly':
          annualAmount = grossAmount * 52;
          break;
        case 'biweekly':
          annualAmount = grossAmount * 26;
          break;
        case 'monthly':
          annualAmount = grossAmount * 12;
          break;
        default:
          console.warn('Unknown frequency:', frequency);
          return;
      }
      
      // Validation: Check for reasonable annual salary limits
      if (annualAmount > 10000000) {
        console.warn('Annual salary exceeds reasonable limit');
      }
      
      setValue('grossAnnualSalary', formatAsCurrency(annualAmount.toString()));
    }
  };

  const handleGrossAnnualSalaryChange = (value) => {
    const annualAmount = parseFloat(String(value || '').replace(/,/g, '')) || 0;
    const frequency = watch('frequency');
    
    // Validation: Check for reasonable annual salary limits
    if (annualAmount < 0) {
      console.warn('Annual salary cannot be negative');
      return;
    }
    if (annualAmount > 10000000) {
      console.warn('Annual salary exceeds reasonable limit');
    }
    
    if (annualAmount && frequency) {
      let grossAmount = 0;
      switch (frequency) {
        case 'weekly':
          grossAmount = annualAmount / 52;
          break;
        case 'biweekly':
          grossAmount = annualAmount / 26;
          break;
        case 'monthly':
          grossAmount = annualAmount / 12;
          break;
        default:
          console.warn('Unknown frequency:', frequency);
          return;
      }
      
      // Validation: Check for reasonable gross amount limits
      if (grossAmount > 1000000) {
        console.warn('Calculated gross amount exceeds reasonable limit');
      }
      
      setValue('grossAmount', formatAsCurrency(grossAmount.toString()));
    }
  };

  const handleFrequencyChange = (newFrequency) => {
    const grossAmount = parseFloat(String(watch('grossAmount') || '').replace(/,/g, '')) || 0;
    const grossAnnualSalary = parseFloat(String(watch('grossAnnualSalary') || '').replace(/,/g, '')) || 0;
    
    if (grossAmount && newFrequency) {
      // Update annual salary based on current gross amount
      let annualAmount = 0;
      switch (newFrequency) {
        case 'weekly':
          annualAmount = grossAmount * 52;
          break;
        case 'biweekly':
          annualAmount = grossAmount * 26;
          break;
        case 'monthly':
          annualAmount = grossAmount * 12;
          break;
      }
      setValue('grossAnnualSalary', formatAsCurrency(annualAmount.toString()));
    } else if (grossAnnualSalary && newFrequency) {
      // Update gross amount based on current annual salary
      let grossAmount = 0;
      switch (newFrequency) {
        case 'weekly':
          grossAmount = grossAnnualSalary / 52;
          break;
        case 'biweekly':
          grossAmount = grossAnnualSalary / 26;
          break;
        case 'monthly':
          grossAmount = grossAnnualSalary / 12;
          break;
      }
      setValue('grossAmount', formatAsCurrency(grossAmount.toString()));
    }
  };
  
  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setSubmissionError(null);
      
      // Prepare all the values with proper formatting
      // Parse money values to numbers for the API
      const formattedData = {
        name: data.name,
        type: 'SALARY',  // Explicitly set type as SALARY
        category: 'SALARY',
        amount: parseCurrency(data.grossAmount),
        grossAmount: parseCurrency(data.grossAmount),
        grossAnnualSalary: parseCurrency(data.grossAnnualSalary),
        netAmount: parseCurrency(data.netAmount),
        frequency: data.frequency.toLowerCase(),
        startDate: data.startDate,
        bucket: bucketId,
        bucketId: bucketId,
        employerName: data.employerName,
        location: {
          state: data.state
        },
        taxWithholdings: {
          federal: parseCurrency(data.taxWithholdings.federal),
          state: parseCurrency(data.taxWithholdings.state),
          medicare: parseCurrency(data.taxWithholdings.medicare),
          socialSecurity: parseCurrency(data.taxWithholdings.socialSecurity)
        },
        deductions: {
          retirement: parseCurrency(data.deductions.retirement),
          health: parseCurrency(data.deductions.health),
          dental: parseCurrency(data.deductions.dental),
          vision: parseCurrency(data.deductions.vision),
          hsa: parseCurrency(data.deductions.hsa),
          fsa: parseCurrency(data.deductions.fsa),
          other: parseCurrency(data.deductions.other)
        },
        w4: {
          filingStatus: data.w4.filingStatus,
          dependents: data.w4.dependents,
          otherIncome: parseCurrency(data.w4.otherIncome),
          deductions: parseCurrency(data.w4.deductions),
          extraWithholding: parseCurrency(data.w4.extraWithholding)
        },
        notes: data.notes,
        // Add typeSpecificDetails to explicitly identify as salary income
        typeSpecificDetails: {
          salary: {
            employer: {
              name: data.employerName,
              location: {
                state: data.state
              }
            },
            w4: {
              filingStatus: data.w4.filingStatus,
              dependents: data.w4.dependents,
              otherIncome: parseCurrency(data.w4.otherIncome),
              deductions: parseCurrency(data.w4.deductions),
              extraWithholding: parseCurrency(data.w4.extraWithholding)
            },
            payDetails: {
              grossAmount: parseCurrency(data.grossAmount),
              grossAnnualSalary: parseCurrency(data.grossAnnualSalary),
              netAmount: parseCurrency(data.netAmount)
            },
            taxWithholdings: {
              federal: parseCurrency(data.taxWithholdings.federal),
              state: parseCurrency(data.taxWithholdings.state),
              medicare: parseCurrency(data.taxWithholdings.medicare),
              socialSecurity: parseCurrency(data.taxWithholdings.socialSecurity)
            },
            deductions: {
              retirement: parseCurrency(data.deductions.retirement),
              health: parseCurrency(data.deductions.health),
              other: parseCurrency(data.deductions.other)
            },
            bonuses: {
              signing: parseCurrency(data.bonuses?.signing),
              retention: parseCurrency(data.bonuses?.retention),
              performance: parseCurrency(data.bonuses?.performance),
              annualTargetPercent: parseCurrency(data.bonuses?.annualTargetPercent),
              annualTargetFixed: parseCurrency(data.bonuses?.annualTargetFixed)
            },
            equity: (() => {
              let vestSchedule = [];
              try {
                vestSchedule = JSON.parse(data.equity?.vestScheduleJson || '[]');
              } catch (_) {}
              return {
                totalRSUShares: parseCurrency(data.equity?.totalRSUShares),
                rsuGrantUsd: parseCurrency(data.equity?.rsuGrantUsd),
                vestStartDate: data.equity?.vestStartDate || undefined,
                vestCliffMonths: data.equity?.vestCliffMonths ? Number(data.equity.vestCliffMonths) : undefined,
                vestMonths: data.equity?.vestMonths ? Number(data.equity.vestMonths) : undefined,
                vestCadence: data.equity?.vestCadence || undefined,
                estimatedSharePrice: parseCurrency(data.equity?.estimatedSharePrice),
                annualValue: parseCurrency(data.equity?.annualValue),
                vestSchedule
              };
            })()
          }
        }
      };
      
      console.log(`📝 ${isEditing ? 'Updating' : 'Creating'} salary income:`, formattedData);
      
      // Use the appropriate function based on isEditing
      if (isEditing && income) {
        await updateIncome(bucketId, income._id, formattedData);
      } else {
        await createIncome(bucketId, formattedData);
      }
      
      setIsSubmitting(false);
      onSuccess();
    } catch (error) {
      console.error('Failed to save salary income:', error);
      setSubmissionError(`Failed to ${isEditing ? 'update' : 'create'} income: ${error.message}`);
      setIsSubmitting(false);
    }
  };
  
  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700 bg-white border border-gray-300 rounded-lg px-3 py-2 mb-4 focus:outline-none group hover:bg-gray-50 transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1 group-hover:-translate-x-0.5 transition-transform" />
        Back to income types
      </button>
      
      {/* Entry method tabs */}
      <div className="flex mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className={`py-3 px-4 text-sm font-medium transition-colors ${
            entryMethod === 'manual'
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          } flex-1`}
          onClick={() => handleTabSelect('manual')}
        >
          Manual Entry
        </button>
        <button
          className={`py-3 px-4 text-sm font-medium transition-colors ${
            entryMethod === 'upload'
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          } flex-1`}
          onClick={() => handleTabSelect('upload')}
        >
          Upload Pay Stub
        </button>
      </div>
      
      {/* Upload Pay Stub UI */}
      {entryMethod === 'upload' && (
        <div className="mb-6">
          {!uploadedFile ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center">
              <DocumentArrowUpIcon className="h-10 w-10 text-gray-400 mb-3" />
              <p className="text-sm text-gray-500 mb-4 text-center">
                Upload your pay stub as a PDF and we'll automatically extract the information
              </p>
              <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer">
                <span>Select Pay Stub</span>
                <input 
                  type="file" 
                  accept=".pdf" 
                  className="sr-only"
                  onChange={handleFileUpload}
                />
              </label>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-indigo-500" />
                </div>
                <div className="ml-3 flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{uploadedFile.name}</p>
                    <button
                      onClick={() => {
                        setUploadedFile(null);
                        setProcessedData(null);
                        setProcessingError(null);
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                    >
                      <XCircleIcon className="h-4 w-4 mr-1" /> Remove
                    </button>
                  </div>
                  
                  {/* Processing state */}
                  {isProcessing ? (
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing document...
                    </div>
                  ) : processingError ? (
                    <div className="mt-2 flex items-center text-sm text-red-600">
                      <XCircleIcon className="h-4 w-4 mr-1" /> {processingError}
                    </div>
                  ) : processedData ? (
                    <div className="mt-2 flex items-center text-sm text-green-600">
                      <CheckCircleIcon className="h-4 w-4 mr-1" /> Data extracted successfully. You can review and edit below.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
          
          {/* Info message about upload */}
          <div className="mt-4 flex items-start bg-blue-50 p-3 rounded-lg">
            <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 ml-2">
              After uploading, you'll still be able to review and edit all the information before saving.
              The extracted data will be filled into the form below.
            </p>
          </div>
        </div>
      )}
      
      {/* Manual Entry UI */}
      {entryMethod === 'manual' && (
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information Section */}
          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Income Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Income Name*
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  placeholder="Primary Job"
                  {...register('name', { required: 'Income name is required' })}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <XCircleIcon className="h-4 w-4 mr-1" /> {errors.name.message}
                  </p>
                )}
              </div>
              
              {/* Employer Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employer
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  placeholder="Company Name"
                  {...register('employerName')}
                />
              </div>
              
              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date*
                </label>
                <div className="relative">
                  <input
                    type="date"
                    className="w-full pl-3 pr-10 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors cursor-pointer"
                    {...register('startDate', { 
                      required: 'Start date is required',
                      validate: {
                        notInFuture: value => {
                          const date = new Date(value);
                          const now = new Date();
                          const tooFarInFuture = new Date();
                          tooFarInFuture.setFullYear(now.getFullYear() + 5); // Max 5 years in future
                          const tooFarInPast = new Date();
                          tooFarInPast.setFullYear(now.getFullYear() - 100); // Max 100 years in past
                          
                          if (date > tooFarInFuture) return 'Date cannot be more than 5 years in the future';
                          if (date < tooFarInPast) return 'Date cannot be more than 100 years in past';
                          return true;
                        }
                      }
                    })}
                    onFocus={(e) => e.target.showPicker()}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <XCircleIcon className="h-4 w-4 mr-1" /> {errors.startDate.message}
                  </p>
                )}
              </div>
              
              {/* State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State*
                </label>
                <select
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  {...register('state', { required: 'State is required' })}
                >
                  <option value="" disabled>Select a state</option>
                  {STATE_OPTIONS.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <XCircleIcon className="h-4 w-4 mr-1" /> {errors.state.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        
        

          {/* Payment Details Section */}
          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Payment Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Gross Annual Salary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                  <span>Gross Annual Salary</span>
                  <InfoIcon text="Annualized gross pay before taxes and deductions." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                    placeholder="0.00"
                    {...register('grossAnnualSalary', {
                      pattern: {
                        value: /^[0-9,]*\.?[0-9]*$/,
                        message: 'Please enter a valid amount'
                      },
                      onChange: (e) => {
                        handleGrossAnnualSalaryChange(e.target.value);
                      }
                    })}
                    onBlur={handleCurrencyBlur('grossAnnualSalary')}
                  />
                </div>
                {errors.grossAnnualSalary && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <XCircleIcon className="h-4 w-4 mr-1" /> {errors.grossAnnualSalary.message}
                  </p>
                )}
              </div>

              {/* Pay Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                  <span>Pay Frequency*</span>
                  <InfoIcon text="How often you are paid for this income." />
                </label>
                <select
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  {...register('frequency', { 
                    required: 'Frequency is required',
                    onChange: (e) => {
                      handleFrequencyChange(e.target.value);
                    }
                  })}
                >
                  <option value="" disabled>Select a frequency</option>
                  {FREQUENCY_OPTIONS.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.frequency && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <XCircleIcon className="h-4 w-4 mr-1" /> {errors.frequency.message}
                  </p>
                )}
              </div>

              {/* Gross Per Pay Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                  <span>Gross Per Pay Period (per {formattedTaxSummary.periodLabel})</span>
                  <InfoIcon text="Gross before taxes and deductions for the selected pay period." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                    placeholder="0.00"
                    {...register('grossAmount', { 
                      required: 'Gross amount is required',
                      pattern: {
                        value: /^[0-9,]*\.?[0-9]*$/,
                        message: 'Please enter a valid amount'
                      },
                      validate: {
                        validAmount: value => {
                          const stringValue = String(value || '');
                          const amount = parseFloat(stringValue.replace(/,/g, ''));
                          return !isNaN(amount) && amount > 0 || 'Please enter a valid amount';
                        }
                      },
                      onChange: (e) => {
                        handleGrossPayChange(e.target.value);
                      }
                    })}
                    onBlur={handleCurrencyBlur('grossAmount')}
                  />
                </div>
                {errors.grossAmount && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <XCircleIcon className="h-4 w-4 mr-1" /> {errors.grossAmount.message}
                  </p>
                )}
              </div>

              {/* Net Annual Salary (estimated) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                  <span>Net Annual Salary (estimated)</span>
                  <InfoIcon text="Estimated annual take-home after taxes and deductions." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-gray-50 text-gray-900 border border-gray-200 rounded-lg"
                    value={(netAnnualPreview || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    readOnly
                    disabled
                  />
                </div>
              </div>

              {/* Net Per Pay Period (estimated) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                  <span>Net Per Pay Period (estimated) (per {formattedTaxSummary.periodLabel})</span>
                  <InfoIcon text="Estimated take-home pay after taxes and deductions for the period." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                    placeholder="0.00"
                    {...register('netAmount', { 
                      pattern: {
                        value: /^[0-9,]*\.?[0-9]*$/,
                        message: 'Please enter a valid amount'
                      }
                    })}
                    onBlur={handleCurrencyBlur('netAmount')}
                  />
                </div>
                {errors.netAmount && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <XCircleIcon className="h-4 w-4 mr-1" /> {errors.netAmount.message}
                  </p>
                )}
              </div>
            </div>

            {/* Bonuses */}
            <div className="mt-4">
              <div className="text-xs font-semibold text-gray-600 mb-2">Bonuses</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Signing Bonus ($) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Signing Bonus</span>
                    <InfoIcon text="One-time signing bonus (optional)." />
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input
                      type="text"
                      className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                      placeholder="0.00"
                      {...register('bonuses.signing')}
                      onBlur={handleCurrencyBlur('bonuses.signing')}
                    />
                  </div>
                </div>
                {/* Retention Bonus ($) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Retention Bonus</span>
                    <InfoIcon text="One-time or scheduled retention bonus (optional)." />
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input
                      type="text"
                      className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                      placeholder="0.00"
                      {...register('bonuses.retention')}
                      onBlur={handleCurrencyBlur('bonuses.retention')}
                    />
                  </div>
                </div>
                {/* Performance Bonus ($) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Performance Bonus</span>
                    <InfoIcon text="Expected annual performance bonus (optional)." />
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input
                      type="text"
                      className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                      placeholder="0.00"
                      {...register('bonuses.performance')}
                      onBlur={handleCurrencyBlur('bonuses.performance')}
                    />
                  </div>
                </div>
                {/* Bonus Targets */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                      <span>Bonus Target %</span>
                      <InfoIcon text="Annual bonus target as a percent of base salary." />
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full pr-8 px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                        placeholder="0"
                        {...register('bonuses.annualTargetPercent')}
                      />
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 z-10">%</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                      <span>Bonus Target Fixed (Annual)</span>
                      <InfoIcon text="Annual bonus target specified as a fixed dollar amount." />
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                      <input
                        type="text"
                        className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                        placeholder="0.00"
                        {...register('bonuses.annualTargetFixed')}
                        onBlur={handleCurrencyBlur('bonuses.annualTargetFixed')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Equity */}
            <div className="mt-4">
              <div className="text-xs font-semibold text-gray-600 mb-2">Equity</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Total RSU Shares */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Total RSU Shares</span>
                    <InfoIcon text="Total number of RSU shares granted." />
                  </label>
                  <input className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg" placeholder="0" {...register('equity.totalRSUShares')} />
                </div>
                {/* RSU Grant Value ($) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>RSU Grant Value ($)</span>
                    <InfoIcon text="Approximate total dollar value of the RSU grant at grant time." />
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg" placeholder="0.00" {...register('equity.rsuGrantUsd')} onBlur={handleCurrencyBlur('equity.rsuGrantUsd')} />
                  </div>
                </div>
                {/* Vesting Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Vesting Start Date</span>
                    <InfoIcon text="Date when RSU vesting begins." />
                  </label>
                  <input className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg" type="date" placeholder="mm/dd/yyyy" {...register('equity.vestStartDate')} />
                </div>
                {/* Cliff Months */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Cliff Months</span>
                    <InfoIcon text="Number of months before the first vest." />
                  </label>
                  <div className="relative">
                    <input className="w-full pr-10 px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg" placeholder="12" {...register('equity.vestCliffMonths')} />
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 z-10">mo</span>
                  </div>
                </div>
                {/* Total Vest Months */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Total Vest Months</span>
                    <InfoIcon text="Total months over which RSUs vest." />
                  </label>
                  <div className="relative">
                    <input className="w-full pr-10 px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg" placeholder="48" {...register('equity.vestMonths')} />
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 z-10">mo</span>
                  </div>
                </div>
                {/* Vesting Cadence */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Vesting Cadence</span>
                    <InfoIcon text="How often RSUs vest after the cliff." />
                  </label>
                  <select className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg" {...register('equity.vestCadence')}>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </div>
                {/* Estimated Share Price ($) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Estimated Share Price ($)</span>
                    <InfoIcon text="Estimated price per share for RSUs (optional)." />
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg" placeholder="0.00" {...register('equity.estimatedSharePrice')} onBlur={handleCurrencyBlur('equity.estimatedSharePrice')} />
                  </div>
                </div>
                {/* Annual Equity Value (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Annual Equity Value (optional)</span>
                    <InfoIcon text="Approximate value of equity vesting per year (optional)." />
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg" placeholder="0.00" {...register('equity.annualValue')} onBlur={handleCurrencyBlur('equity.annualValue')} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
      
          
          {/* W4 Information Section */}
          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-gray-700">W4 Information</h3>
              <span className="text-xs text-gray-500">Used to estimate tax withholdings</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Filing Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filing Status
                </label>
                <select
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  {...register('w4.filingStatus')}
                >
                  <option value="SINGLE">Single or Married filing separately</option>
                  <option value="MARRIED">Married filing jointly</option>
                  <option value="HEAD">Head of household</option>
                </select>
              </div>

              {/* Number of Dependents */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Qualifying Dependents
                </label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  placeholder="0"
                  {...register('w4.dependents')}
                />
              </div>

              {/* Other Income */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Other Annual Income (not from jobs)
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                    placeholder="0.00"
                    {...register('w4.otherIncome')}
                    onBlur={handleCurrencyBlur('w4.otherIncome')}
                  />
                </div>
              </div>

              {/* Deductions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Annual Deductions
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                    placeholder="0.00"
                    {...register('w4.deductions')}
                    onBlur={handleCurrencyBlur('w4.deductions')}
                  />
                </div>
              </div>

              {/* Extra Withholding */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Extra Withholding Per Pay Period
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                    placeholder="0.00"
                    {...register('w4.extraWithholding')}
                    onBlur={handleCurrencyBlur('w4.extraWithholding')}
                  />
                </div>
              </div>
            </div>
          </div>
          
          
          
          {/* Deductions Section */}
          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Deductions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Column 1: Health & Benefits */}
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2">Health & Benefits</div>
                {/* Health Insurance */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Health Insurance (per {formattedTaxSummary.periodLabel})</span>
                    <InfoIcon text="Employee health premium per period." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input type="text" className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors" placeholder="0.00" {...register('deductions.health')} onBlur={handleCurrencyBlur('deductions.health')} />
                </div>
              </div>
                {/* Dental */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Dental Insurance (per {formattedTaxSummary.periodLabel})</span>
                    <InfoIcon text="Employee dental premium per period." />
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input type="text" className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors" placeholder="0.00" {...register('deductions.dental')} onBlur={handleCurrencyBlur('deductions.dental')} />
                  </div>
                </div>
                {/* Vision */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Vision Insurance (per {formattedTaxSummary.periodLabel})</span>
                    <InfoIcon text="Employee vision premium per period." />
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input type="text" className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors" placeholder="0.00" {...register('deductions.vision')} onBlur={handleCurrencyBlur('deductions.vision')} />
                  </div>
                </div>
                {/* HSA */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>HSA Contribution (per {formattedTaxSummary.periodLabel})</span>
                    <InfoIcon text="Employee HSA contribution per period (pre-tax)." />
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input type="text" className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors" placeholder="0.00" {...register('deductions.hsa')} onBlur={handleCurrencyBlur('deductions.hsa')} />
                  </div>
                </div>
                {/* FSA */}
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>FSA Contribution (per {formattedTaxSummary.periodLabel})</span>
                    <InfoIcon text="Employee FSA contribution per period (pre-tax)." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input type="text" className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors" placeholder="0.00" {...register('deductions.fsa')} onBlur={handleCurrencyBlur('deductions.fsa')} />
                  </div>
                </div>
              </div>
              
              {/* Column 2: Retirement & Other */}
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2">Retirement & Other</div>
                {/* Retirement */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Retirement (401k, IRA, etc.) (per {formattedTaxSummary.periodLabel})</span>
                    <InfoIcon text="Employee retirement contribution per period." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input type="text" className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors" placeholder="0.00" {...register('deductions.retirement')} onBlur={handleCurrencyBlur('deductions.retirement')} />
                  </div>
                </div>
                {/* Other */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                    <span>Other Deductions (per {formattedTaxSummary.periodLabel})</span>
                    <InfoIcon text="Any additional per-period deductions." />
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                    <input type="text" className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors" placeholder="0.00" {...register('deductions.other')} onBlur={handleCurrencyBlur('deductions.other')} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Tax Withholdings Section (placed after Deductions) */}
          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Tax Withholdings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Federal Tax */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                  <span>Federal Tax (per {formattedTaxSummary.periodLabel})</span>
                  <InfoIcon text="Estimated federal income tax withheld per period." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                    placeholder="0.00"
                    {...register('taxWithholdings.federal')}
                    onBlur={handleCurrencyBlur('taxWithholdings.federal')}
                  />
                </div>
              </div>
              
              {/* State Tax */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                  <span>State Tax (per {formattedTaxSummary.periodLabel})</span>
                  <InfoIcon text="Estimated state income tax withheld per period." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                    placeholder="0.00"
                    {...register('taxWithholdings.state')}
                    onBlur={handleCurrencyBlur('taxWithholdings.state')}
                  />
                </div>
              </div>
              
              {/* Medicare */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                  <span>Medicare (per {formattedTaxSummary.periodLabel})</span>
                  <InfoIcon text="Employee Medicare tax withheld per period." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                    placeholder="0.00"
                    {...register('taxWithholdings.medicare')}
                    onBlur={handleCurrencyBlur('taxWithholdings.medicare')}
                  />
                </div>
              </div>
              
              {/* Social Security */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                  <span>Social Security (per {formattedTaxSummary.periodLabel})</span>
                  <InfoIcon text="Employee Social Security tax withheld per period." />
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                    placeholder="0.00"
                    {...register('taxWithholdings.socialSecurity')}
                    onBlur={handleCurrencyBlur('taxWithholdings.socialSecurity')}
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Notes Section */}
          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Notes (Optional)</h3>
            <textarea
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
              rows="3"
              placeholder="Add any additional notes about this income"
              {...register('notes')}
            ></textarea>
          </div>
          
          {/* Tax Preview */}
          {watchedGrossAmount && parseFloat(watchedGrossAmount) > 0 && (
            <div className="border-l-4 border-blue-400 bg-blue-50 p-4 rounded-r-lg mb-6">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">Estimated Tax Breakdown</h4>
              
              <div className="flex mb-2">
                <div className="w-1/3"></div>
                <div className="w-1/3">
                  <div className="text-blue-800 font-semibold mb-1 text-center">Per {formattedTaxSummary.periodLabel}</div>
                </div>
                <div className="w-1/3">
                  <div className="text-blue-800 font-semibold mb-1 text-center">Annual</div>
                </div>
              </div>
              
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <div className="text-blue-700 w-1/3">Federal Withholding:</div>
                  <div className="text-blue-800 font-medium w-1/3 text-center">{formattedTaxSummary.periodFederalWithholding}</div>
                  <div className="text-blue-800 font-medium w-1/3 text-center">{formattedTaxSummary.federalWithholding}</div>
                </div>
                
                <div className="flex justify-between">
                  <div className="text-blue-700 w-1/3">State Withholding:</div>
                  <div className="text-blue-800 font-medium w-1/3 text-center">{formattedTaxSummary.periodStateWithholding}</div>
                  <div className="text-blue-800 font-medium w-1/3 text-center">{formattedTaxSummary.stateWithholding}</div>
                </div>
                
                <div className="flex justify-between">
                  <div className="text-blue-700 w-1/3">Medicare (MED/EE):</div>
                  <div className="text-blue-800 font-medium w-1/3 text-center">{formattedTaxSummary.periodMedicareEE}</div>
                  <div className="text-blue-800 font-medium w-1/3 text-center">{formattedTaxSummary.medicareEE}</div>
                </div>
                
                <div className="flex justify-between">
                  <div className="text-blue-700 w-1/3">Social Security (OASDI/EE):</div>
                  <div className="text-blue-800 font-medium w-1/3 text-center">{formattedTaxSummary.periodSocialSecurityEE}</div>
                  <div className="text-blue-800 font-medium w-1/3 text-center">{formattedTaxSummary.socialSecurityEE}</div>
                </div>
                
                <div className="flex justify-between pt-1 border-t border-blue-200">
                  <div className="text-blue-700 font-medium w-1/3">Total Tax Withholdings:</div>
                  <div className="text-blue-800 font-medium w-1/3 text-center">{formattedTaxSummary.periodTotalWithholdings}</div>
                  <div className="text-blue-800 font-medium w-1/3 text-center">{formattedTaxSummary.totalWithholdings}</div>
                </div>
              </div>
              
              <p className="text-xs text-blue-600 mt-2">
                This is an estimate based on the information provided. Your actual tax withholdings may vary.
              </p>
            </div>
          )}

          {/* Estimated Total Compensation (kept at bottom) */}
          <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded">
            <div className="text-sm font-semibold text-emerald-800">Estimated Total Compensation</div>
            <div className="text-xs text-emerald-700 mt-1">Base: ${baseAnnualPreview.toLocaleString()} • Bonus: ${annualBonusPreview.toLocaleString()} • One-time (annualized): ${oneTimeBonusesAnnualized.toLocaleString()} • Equity: ${equityAnnualEstimated.toLocaleString()}</div>
            <div className="text-sm font-bold text-emerald-900 mt-1">Total Annual: ${totalCompAnnual.toLocaleString()}</div>
          </div>
          
          {/* Submission Error */}
          {submissionError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-5 w-5 text-red-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{submissionError}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting && (
                <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isSubmitting 
                ? `${isEditing ? 'Updating' : 'Adding'} Income...` 
                : `${isEditing ? 'Update' : 'Add'} Income`
              }
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SalaryForm; 